import React, { useEffect, useState } from 'react';
import { vehiclesApi } from '../api/client';
import type { Vehicle } from '../api/client';
import './Vehicles.css';

const Vehicles: React.FC = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newVehicle, setNewVehicle] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    color: '',
    vin: '',
    licensePlate: '',
    mileage: '',
    fuelType: 'gasoline' as 'gasoline' | 'diesel' | 'electric' | 'hybrid' | 'plug-in-hybrid',
    transmission: 'automatic' as 'automatic' | 'manual' | 'cvt',
    notes: ''
  });

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const response = await vehiclesApi.getVehicles();
      setVehicles(response.data);
    } catch (err) {
      setError('Failed to load vehicles');
      console.error('Vehicles error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddVehicle = async () => {
    if (!newVehicle.make.trim() || !newVehicle.model.trim()) return;

    try {
      const vehicleData = {
        make: newVehicle.make,
        model: newVehicle.model,
        year: newVehicle.year,
        color: newVehicle.color || undefined,
        vin: newVehicle.vin || undefined,
        licensePlate: newVehicle.licensePlate || undefined,
        mileage: newVehicle.mileage ? parseInt(newVehicle.mileage) : undefined,
        fuelType: newVehicle.fuelType,
        transmission: newVehicle.transmission,
        notes: newVehicle.notes || undefined
      };

      await vehiclesApi.createVehicle(vehicleData);
      await fetchVehicles();
      setShowAddModal(false);
      resetNewVehicle();
    } catch (err) {
      console.error('Error adding vehicle:', err);
      setError('Failed to add vehicle');
    }
  };

  const handleDeleteVehicle = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) return;

    try {
      await vehiclesApi.deleteVehicle(id);
      await fetchVehicles();
    } catch (err) {
      console.error('Error deleting vehicle:', err);
      setError('Failed to delete vehicle');
    }
  };

  const resetNewVehicle = () => {
    setNewVehicle({
      make: '',
      model: '',
      year: new Date().getFullYear(),
      color: '',
      vin: '',
      licensePlate: '',
      mileage: '',
      fuelType: 'gasoline',
      transmission: 'automatic',
      notes: ''
    });
  };

  const getVehicleIcon = (fuelType: string) => {
    switch (fuelType) {
      case 'electric': return '⚡';
      case 'hybrid': return '🔋';
      case 'plug-in-hybrid': return '🔌';
      case 'diesel': return '🛢️';
      default: return '⛽';
    }
  };

  const getFuelTypeColor = (fuelType: string) => {
    switch (fuelType) {
      case 'electric': return '#27ae60';
      case 'hybrid': return '#3498db';
      case 'plug-in-hybrid': return '#2ecc71';
      case 'diesel': return '#34495e';
      default: return '#e67e22';
    }
  };

  const getMaintenanceStatus = (vehicle: Vehicle) => {
    if (!vehicle.maintenance || vehicle.maintenance.length === 0) {
      return { status: 'unknown', color: '#95a5a6', text: 'No Records' };
    }

    const lastMaintenance = vehicle.maintenance
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
    
    const daysSince = Math.floor((Date.now() - new Date(lastMaintenance.date).getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysSince < 30) {
      return { status: 'recent', color: '#27ae60', text: `${daysSince} days ago` };
    } else if (daysSince < 90) {
      return { status: 'moderate', color: '#f39c12', text: `${daysSince} days ago` };
    } else {
      return { status: 'overdue', color: '#e74c3c', text: `${daysSince} days ago` };
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return <div className="vehicles-page"><div className="loading">Loading vehicles...</div></div>;
  }

  if (error) {
    return <div className="vehicles-page"><div className="error">Error: {error}</div></div>;
  }

  return (
    <div className="vehicles-page">
      <header className="page-header">
        <h1>🚗 Vehicles</h1>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowAddModal(true)}
        >
          Add New Vehicle
        </button>
      </header>

      {error && <div className="error-message">{error}</div>}

      <div className="vehicles-stats">
        <div className="stat-card">
          <span className="stat-number">{vehicles.length}</span>
          <span className="stat-label">Total Vehicles</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{vehicles.filter(v => v.fuelType === 'electric' || v.fuelType === 'hybrid').length}</span>
          <span className="stat-label">Eco-Friendly</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">
            {vehicles.reduce((acc, v) => acc + (v.maintenance?.length || 0), 0)}
          </span>
          <span className="stat-label">Maintenance Records</span>
        </div>
      </div>

      <div className="vehicles-grid">
        {vehicles.map((vehicle) => {
          const maintenanceStatus = getMaintenanceStatus(vehicle);
          
          return (
            <div key={vehicle.id} className="vehicle-card">
              <div className="vehicle-header">
                <div className="vehicle-title">
                  <span className="vehicle-icon">{getVehicleIcon(vehicle.fuelType || 'gasoline')}</span>
                  <div className="vehicle-info">
                    <h3 className="vehicle-name">
                      {vehicle.year} {vehicle.make} {vehicle.model}
                    </h3>
                    {vehicle.color && (
                      <span className="vehicle-color">{vehicle.color}</span>
                    )}
                  </div>
                </div>
                <button 
                  className="delete-btn"
                  onClick={() => handleDeleteVehicle(vehicle.id)}
                  title="Delete vehicle"
                >
                  ×
                </button>
              </div>

              <div className="vehicle-content">
                <div className="vehicle-details">
                  <div className="detail-row">
                    <span className="detail-label">Fuel Type:</span>
                    <span 
                      className="fuel-badge"
                      style={{ backgroundColor: getFuelTypeColor(vehicle.fuelType || 'gasoline') }}
                    >
                      {vehicle.fuelType || 'gasoline'}
                    </span>
                  </div>

                  <div className="detail-row">
                    <span className="detail-label">Transmission:</span>
                    <span className="detail-value">{vehicle.transmission || 'automatic'}</span>
                  </div>

                  {vehicle.mileage && (
                    <div className="detail-row">
                      <span className="detail-label">Mileage:</span>
                      <span className="detail-value">{vehicle.mileage.toLocaleString()} miles</span>
                    </div>
                  )}

                  {vehicle.licensePlate && (
                    <div className="detail-row">
                      <span className="detail-label">License Plate:</span>
                      <span className="detail-value license-plate">{vehicle.licensePlate}</span>
                    </div>
                  )}

                  {vehicle.vin && (
                    <div className="detail-row">
                      <span className="detail-label">VIN:</span>
                      <span className="detail-value vin">{vehicle.vin}</span>
                    </div>
                  )}
                </div>

                {vehicle.insurance && (
                  <div className="insurance-info">
                    <h4 className="section-title">Insurance</h4>
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="info-label">Provider:</span>
                        <span className="info-value">{vehicle.insurance.provider}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Expires:</span>
                        <span className="info-value">
                          {new Date(vehicle.insurance.expirationDate).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {vehicle.maintenance && vehicle.maintenance.length > 0 && (
                  <div className="maintenance-info">
                    <div className="maintenance-header">
                      <h4 className="section-title">Maintenance</h4>
                      <span 
                        className="maintenance-status"
                        style={{ color: maintenanceStatus.color }}
                      >
                        Last: {maintenanceStatus.text}
                      </span>
                    </div>
                    <div className="maintenance-recent">
                      {vehicle.maintenance
                        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                        .slice(0, 2)
                        .map((maintenance, index) => (
                          <div key={index} className="maintenance-item">
                            <div className="maintenance-type">{maintenance.type}</div>
                            <div className="maintenance-details">
                              <span className="maintenance-date">
                                {new Date(maintenance.date).toLocaleDateString()}
                              </span>
                              <span className="maintenance-cost">
                                {formatCurrency(maintenance.cost)}
                              </span>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {vehicle.purchaseInfo && (
                  <div className="purchase-info">
                    <h4 className="section-title">Purchase Info</h4>
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="info-label">Date:</span>
                        <span className="info-value">
                          {new Date(vehicle.purchaseInfo.date).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Price:</span>
                        <span className="info-value purchase-price">
                          {formatCurrency(vehicle.purchaseInfo.price)}
                        </span>
                      </div>
                      {vehicle.purchaseInfo.dealer && (
                        <div className="info-item">
                          <span className="info-label">Dealer:</span>
                          <span className="info-value">{vehicle.purchaseInfo.dealer}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {vehicle.notes && (
                  <div className="vehicle-notes">
                    <span className="notes-icon">📝</span>
                    <p className="notes-text">{vehicle.notes}</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {vehicles.length === 0 && (
          <div className="no-vehicles">
            <span className="no-vehicles-icon">🚗</span>
            <p>No vehicles added yet. Add your first vehicle to get started!</p>
          </div>
        )}
      </div>

      {/* Add Vehicle Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Vehicle</h3>
              <button 
                className="modal-close"
                onClick={() => setShowAddModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Make *</label>
                  <input
                    type="text"
                    value={newVehicle.make}
                    onChange={(e) => setNewVehicle({...newVehicle, make: e.target.value})}
                    className="form-input"
                    placeholder="e.g., Toyota, Honda, Tesla..."
                  />
                </div>

                <div className="form-group">
                  <label>Model *</label>
                  <input
                    type="text"
                    value={newVehicle.model}
                    onChange={(e) => setNewVehicle({...newVehicle, model: e.target.value})}
                    className="form-input"
                    placeholder="e.g., Camry, Civic, Model 3..."
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Year</label>
                  <input
                    type="number"
                    min="1900"
                    max={new Date().getFullYear() + 1}
                    value={newVehicle.year}
                    onChange={(e) => setNewVehicle({...newVehicle, year: parseInt(e.target.value)})}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Color</label>
                  <input
                    type="text"
                    value={newVehicle.color}
                    onChange={(e) => setNewVehicle({...newVehicle, color: e.target.value})}
                    className="form-input"
                    placeholder="e.g., Silver, Black, White..."
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Fuel Type</label>
                  <select 
                    value={newVehicle.fuelType}
                    onChange={(e) => setNewVehicle({...newVehicle, fuelType: e.target.value as any})}
                    className="form-select"
                  >
                    <option value="gasoline">Gasoline</option>
                    <option value="diesel">Diesel</option>
                    <option value="electric">Electric</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="plug-in-hybrid">Plug-in Hybrid</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Transmission</label>
                  <select 
                    value={newVehicle.transmission}
                    onChange={(e) => setNewVehicle({...newVehicle, transmission: e.target.value as any})}
                    className="form-select"
                  >
                    <option value="automatic">Automatic</option>
                    <option value="manual">Manual</option>
                    <option value="cvt">CVT</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>VIN</label>
                  <input
                    type="text"
                    value={newVehicle.vin}
                    onChange={(e) => setNewVehicle({...newVehicle, vin: e.target.value})}
                    className="form-input"
                    placeholder="17-character VIN..."
                    maxLength={17}
                  />
                </div>

                <div className="form-group">
                  <label>License Plate</label>
                  <input
                    type="text"
                    value={newVehicle.licensePlate}
                    onChange={(e) => setNewVehicle({...newVehicle, licensePlate: e.target.value})}
                    className="form-input"
                    placeholder="ABC-1234"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Current Mileage</label>
                <input
                  type="number"
                  min="0"
                  value={newVehicle.mileage}
                  onChange={(e) => setNewVehicle({...newVehicle, mileage: e.target.value})}
                  className="form-input"
                  placeholder="Enter current mileage..."
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={newVehicle.notes}
                  onChange={(e) => setNewVehicle({...newVehicle, notes: e.target.value})}
                  className="form-textarea"
                  rows={3}
                  placeholder="Additional notes about this vehicle..."
                />
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn btn-success"
                onClick={handleAddVehicle}
                disabled={!newVehicle.make.trim() || !newVehicle.model.trim()}
              >
                Add Vehicle
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vehicles;